import { Typography } from "@mui/material";
import moment from "moment";
import React, { useMemo } from "react";
import { Datagrid, DateField, Filter, FunctionField, NumberField, ReferenceField, ReferenceInput, SelectInput, TextField, TextInput, useGetList, useGetOne, useListContext } from "react-admin";
import { PartiallyHiddenCpfField, PhoneField } from "../../components/Fields";
import ListPagination from "../../components/ListPagination";
import Permission from "../../components/Permission";
import ReportList from "../../components/ReportList";
import { PERMISSIONS } from "../../constants";
import { formatReal } from "../../lib";
import { GiftProductType, GiftRuleType, GiftValueType } from "../../models";

const titleSx = {
  margin: ".5rem 0 .5rem 0",
  textAlign: "center",
  fontWeight: "bold"
};

const CustomersWithoutDrawingList = () => {
  return (
    <>
      <Typography sx={titleSx}>Relatório de Clientes sem Número da Sorte</Typography>
      <Datagrid bulkActionButtons={false}>
        <TextField label="Nome" source="name" />
        <PartiallyHiddenCpfField label="CPF" source="cpf" />
        <PhoneField label="Telefone" source="cellphone" />
        <NumberField label="Total abastecido" source="amount" textAlign="center" options={{ maximumFractionDigits: 2 }} />
        <NumberField label="Números" source="numbers" textAlign="center" emptyText="-" />
        <NumberField label="Total restante" source="unusedAmount" textAlign="center" emptyText="-" options={{ maximumFractionDigits: 2 }} sortable={false} />
      </Datagrid>
    </>
  );
};

const CustomWithoutExporter = async (data) => {
  return data.map(({ name, cpf, cellphone, amount, numbers, unusedAmount }) => ({ Nome: name, CPF: cpf, Telefone: cellphone, totalAbastecido: amount, numeros: numbers, totalRestante: unusedAmount }));
};

const formatValueToDrawingRule = (drawingRuleFuel) => {
  if (!drawingRuleFuel.value) { return ''; }

  if (drawingRuleFuel.drawingRule && drawingRuleFuel.drawingRule.name === 'fillin-value') {
    return `- ${formatReal(drawingRuleFuel.value)}`;
  }

  return `- ${drawingRuleFuel.value}L`;
}

// Regra do Clube: value_type 'cents' JÁ ESTÁ EM REAIS — nunca dividir por 100.
const formatValueToGiftRule = (giftRule) => {
  if (!giftRule.value) { return ''; }

  return giftRule.valueType === GiftValueType.volume
    ? `- ${giftRule.value}L`
    : `- ${formatReal(giftRule.value)}`;
};

// Sorteio com "Regras via Clube de Vantagens" (isClubDriven) não tem drawing_rule_fuel:
// quem decide quem ganha número é o brinde. Exigir o seletor de regra do sorteio deixaria
// o relatório permanentemente vazio para ele.
const useDrawingIsClubDriven = (drawingId) => {
  const { data: drawing } = useGetOne(
    'chains/chainId/drawings',
    { id: drawingId || '' },
    { enabled: !!drawingId },
  );

  return !!drawing?.isClubDriven;
};

// As regras do sorteio club-driven são as gift_rule de abastecimento dos brindes que têm
// um produto "Número da Sorte" apontando para ele. O backend aceita `giftRuleId` no lugar
// de `drawingRuleFuelId`; sem ele assume a regra mais antiga.
const useClubGiftRuleChoices = (drawingId, enabled) => {
  const { data: gifts } = useGetList(
    'gifts',
    {
      pagination: { page: 1, perPage: 200 },
      sort: { field: 'createdAt', order: 'ASC' },
      filter: { isEnabled: 'all' },
    },
    { enabled: !!enabled && !!drawingId },
  );

  return useMemo(() => {
    if (!enabled || !drawingId || !gifts) { return []; }

    return gifts
      .filter(gift => (gift.giftProducts || []).some(giftProduct =>
        giftProduct.productType === GiftProductType.DrawingNumber
        && giftProduct.drawingId === drawingId))
      .flatMap(gift => (gift.giftRules || [])
        .filter(giftRule => GiftRuleType.fillinTypes.includes(giftRule.type))
        .map(giftRule => ({
          id: giftRule.id,
          name: `${gift.name} - ${GiftRuleType.translateType(giftRule.type)} ${formatValueToGiftRule(giftRule)}`.trim(),
        })));
  }, [gifts, drawingId, enabled]);
};

const FilterTablePrint = () => {
  const { filterValues } = useListContext();
  const isClubDriven = useDrawingIsClubDriven(filterValues.drawingId);
  const clubGiftRuleChoices = useClubGiftRuleChoices(filterValues.drawingId, isClubDriven);
  return (
    <Datagrid bulkActionButtons={false} data={[{ ...filterValues, generatedReportAt: moment().toISOString() }]} total={1}>
      <ReferenceField
        label="Sorteio"
        source="drawingId"
        reference="chains/chainId/drawings"
      >
        <TextField
          source="name"
        />
      </ReferenceField>
      {isClubDriven ? (
        <FunctionField
          label="Regra do Clube de Vantagens"
          render={record => clubGiftRuleChoices.find(({ id }) => id === record.giftRuleId)?.name || 'Primeira regra do brinde (padrão)'}
        />
      ) : (
        <ReferenceField
          label="Regra - Combustível - Valor"
          source="drawingRuleFuelId"
          reference="drawing-rule-fuels"
        >
          <FunctionField
            render={record => `${record.drawingRule.label} - ${record.fuel?.name || 'Todos'} ${formatValueToDrawingRule(record.value)}`}
          />
        </ReferenceField>
      )}
      <NumberField source="amountFrom" label={isClubDriven ? "Restante mín." : "Litros Min."} />
      <NumberField source="amountTo" label={isClubDriven ? "Restante máx." : "Litros Máx."} />
      <DateField source="generatedReportAt" textAlign="center" label="Gerado às" showTime sortable={false} locales="pt-BR" />
    </Datagrid>
  );
};

const FilterReportList = (props) => {
  const { filterValues } = useListContext();
  const isClubDriven = useDrawingIsClubDriven(filterValues.drawingId);
  const clubGiftRuleChoices = useClubGiftRuleChoices(filterValues.drawingId, isClubDriven);
  return (
    <Filter {...props}>
      <ReferenceInput
        source="drawingId"
        reference="chains/chainId/drawings"
        sort={{ field: "name", order: "ASC" }}
        filter={{ status: 'not-finished' }}
        perPage={false}
        alwaysOn
        allowEmpty={false}
      >
        <SelectInput
          allowEmpty={false}
          label="Sorteio"
          style={{ minWidth: 180 }}
          optionText={'name'}
        />
      </ReferenceInput>
      {filterValues.drawingId && !isClubDriven && (
        <ReferenceInput
          source="drawingRuleFuelId"
          reference="drawing-rule-fuels"
          filter={{ drawingId: filterValues.drawingId, accumulated: true }}
          sort={{ field: "startDate", order: "ASC" }}
          perPage={false}
          alwaysOn
        >
          <SelectInput
            allowEmpty={false}
            label="Regra - Combustível - Valor"
            style={{ width: 300, marginTop: 0 }}
            optionText={record => `${record.drawingRule.label} - ${record.fuel?.name || 'Todos'} ${formatValueToDrawingRule(record)}`}
          />
        </ReferenceInput>
      )}
      {/* Sorteio club-driven: a regra é opcional. Vazio = a regra mais antiga do brinde,
          que é o caso normal (um brinde de Número da Sorte por sorteio). */}
      {isClubDriven && clubGiftRuleChoices.length > 0 && (
        <SelectInput
          source="giftRuleId"
          label="Regra do Clube de Vantagens"
          style={{ width: 300, marginTop: 0 }}
          choices={clubGiftRuleChoices}
          emptyText="Primeira regra do brinde (padrão)"
          alwaysOn
        />
      )}
      {/* No sorteio club-driven a regra pode ser em REAIS (value_type 'cents'), então o
          rótulo "Litros" mentiria — a faixa é sempre sobre o total restante. */}
      <TextInput
        style={{ width: 150 }}
        source="amountFrom"
        label={isClubDriven ? "Restante mín." : "Litros Min."}
        format={value => !value ? '' : `${Number(value).toFixed(2)}`}
        parse={value => !value ? 0 : +value.replace(/[^\d]/g, '') / 100}
        alwaysOn
      />
      <TextInput
        style={{ width: 150 }}
        source="amountTo"
        label={isClubDriven ? "Restante máx." : "Litros Máx."}
        format={value => !value ? '' : `${Number(value).toFixed(2)}`}
        parse={value => !value ? 0 : +value.replace(/[^\d]/g, '') / 100}
        alwaysOn
      />
    </Filter>
  );
};

const CustomersWithoutDrawing = (props) => {

  return (
    <Permission permission={PERMISSIONS.CUSTOMERS_WITHOUT_DRAWING_REPORT}>
      <ReportList
        {...props}
        basePath="report-customers-without-drawing"
        title="Clientes sem número da sorte"
        resource="chains/chainId/reports/customers-without-drawing"
        sort={{ field: "name", order: "ASC" }}
        perPage={25}
        filters={<FilterReportList />}
        pagination={<ListPagination />}
        bulkActionButtons={false}
        customExporter={CustomWithoutExporter}
        titleOnPrint="Relatório de clientes sem número da sorte"
        filterTablePrint={<FilterTablePrint />}
        landscape
      >
        <CustomersWithoutDrawingList />
      </ReportList>
    </Permission >
  );
};

export default CustomersWithoutDrawing;